import { ActivitySubjects, Search } from "@outschool/business-rules";
import { EnglishProficiencyLevel } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { slugifyTitle } from "@outschool/routes";
import {
  AUTO_SCHEDULE_DAY_END_HOUR,
  AUTO_SCHEDULE_DAY_START_HOUR,
  briefDurationStringWithoutTimes,
  shortWeekdayRange,
  startDayShortString,
} from "@outschool/time";
import ldfilter from "lodash/filter";
import ldisEqual from "lodash/isEqual";
import ldlast from "lodash/last";
import ldpick from "lodash/pick";
import ldrange from "lodash/range";
import ldslice from "lodash/slice";
import ldupperFirst from "lodash/upperFirst";

import useActivityString from "../../translations/useActivityString";
import { useTranslateRelativeDateFilter } from "./useTranslateRelativeDateFilter";

function dowStringToArray(dowString: string) {
  return dowString
    .trim()
    .split(",")
    .map(d => d.trim())
    .filter(d => d !== "");
}

function dowAreConsecutive(dowArray: string[]) {
  const start = Search.DAYS_OF_WEEK.indexOf(dowArray[0]);
  return ldisEqual(
    dowArray,
    ldslice(Search.DAYS_OF_WEEK, start, start + dowArray.length)
  );
}

const dowIndex = (dow: string) => Search.DAYS_OF_WEEK.indexOf(dow);
const sortDow = (daysOfWeek: string[]) =>
  daysOfWeek.sort((a, b) => dowIndex(a) - dowIndex(b));

export default function useSearchFiltersString() {
  const { t } = useTranslation(
    "ui-components-website\\src\\Search\\translations\\useSearchFilterString"
  );
  const { translateThemeName } = useActivityString();
  const translateRelativeDateFilter = useTranslateRelativeDateFilter();

  function dowSummary(dowString?: string | null, shortDowSummary = true) {
    if (!dowString) {
      return null;
    }
    const daysOfWeek = sortDow(dowStringToArray(dowString));
    const daysOfWeekLabels = daysOfWeek.map(Search.formatDayOfWeek);
    if (daysOfWeek.length === 1) {
      return daysOfWeekLabels[0];
    } else if (ldisEqual([...daysOfWeek].sort(), [...Search.WEEKDAYS].sort())) {
      return t("Weekdays");
    } else if (
      ldisEqual([...daysOfWeek].sort(), [...Search.WEEKEND_DAYS].sort())
    ) {
      return t("Weekends");
    } else if (dowAreConsecutive(daysOfWeek)) {
      const lastDay = ldlast(daysOfWeek);
      return shortWeekdayRange(
        dowIndex(daysOfWeek[0]),
        lastDay ? dowIndex(lastDay) : -1
      );
    } else {
      if (!shortDowSummary) {
        return daysOfWeekLabels.join(", ");
      }
      return t("Some days");
    }
  }

  function dayRangeToString(
    startAfter?: string | null,
    endBy?: string | null
  ): string | undefined {
    const startAfterDayjs = Search.dateFilterToDayjs(startAfter);
    const endByDayjs = Search.dateFilterToDayjs(endBy);

    const startAfterDateString = startDayShortString(startAfter);
    const endByDateString = startDayShortString(endBy);

    if (startAfterDayjs && !endByDayjs) {
      return t("from {{startAfterDateString}}", { startAfterDateString });
    } else if (!startAfterDayjs && endByDayjs) {
      return t("until {{endByDateString}}", { endByDateString });
    } else if (startAfterDayjs && endByDayjs) {
      if (startAfterDayjs.isSame(endByDayjs, "day")) {
        return startAfterDateString;
      } else {
        return briefDurationStringWithoutTimes(startAfter, endBy);
      }
    } else {
      return undefined;
    }
  }

  function scheduleFiltersToString({
    dow,
    startAfter,
    startBefore,
    endBy,
    startAfterTime,
    endByTime,
    shortDowSummary = true,
  }: {
    dow?: string | null;
    startAfter?: string | null;
    startBefore?: string | null;
    endBy?: string | null;
    startAfterTime?: number | null;
    endByTime?: number | null;
    shortDowSummary?: boolean;
  } = {}): string {
    const dowString = dowSummary(dow, shortDowSummary) || "";
    const dayRangeString =
      dayRangeToString(startAfter, endBy || startBefore) || "";
    const timeRangeString =
      Search.timeRangeToString(startAfterTime, endByTime) || "";

    return ldupperFirst(
      ldfilter([
        `${dowString}${
          dowString && dayRangeString ? ", " : ""
        }${dayRangeString}`,
        timeRangeString,
      ]).join(" ")
    );
  }

  function englishProficiencyLevelToString(
    level: EnglishProficiencyLevel | undefined | null
  ) {
    switch (level) {
      case EnglishProficiencyLevel.A0:
        return t("English: A0");
      case EnglishProficiencyLevel.A05:
        return t("English: A0.5");
      case EnglishProficiencyLevel.PreA1:
        return t("English: Pre-A1");
      case EnglishProficiencyLevel.A1:
        return t("English: A1");
      case EnglishProficiencyLevel.A2:
        return t("English: A2");
      case EnglishProficiencyLevel.B1:
        return t("English: B1");
      case EnglishProficiencyLevel.B2:
        return t("English: B2");
      default:
        return undefined;
    }
  }
  return {
    dowSummary,
    scheduleFiltersToString,
    englishProficiencyLevelToString,
    ORDER_OPTIONS: [
      { label: t("Relevance"), value: "relevance" },
      { label: t("New classes"), value: "new" },
      { label: t("Starting soon"), value: "upcoming" },
    ],

    SAVED_ORDER_OPTIONS: [
      {
        label: t("Most recently saved"),
        value: Search.SAVED_ORDER_OPTION_DEFAULT,
      },
      { label: t("Starting soon"), value: "upcoming" },
      { label: t("Most popular"), value: "popular" },
    ],

    startAfterTimeOptions(end: string | number, showLabel = true) {
      const hours = ldrange(
        AUTO_SCHEDULE_DAY_START_HOUR,
        (typeof end === "string" ? parseInt(end, 10) : end) ||
          AUTO_SCHEDULE_DAY_END_HOUR - 1
      );
      return hours.map(hour => {
        return {
          label: showLabel
            ? t("Start after {{time}}", {
                time: Search.displayTimeFromCardinalHour(hour),
              })
            : Search.displayTimeFromCardinalHour(hour),
          value: hour,
        };
      });
    },

    endByTimeOptions(start: string | number, showLabel = true) {
      const hours = ldrange(
        (typeof start === "string" ? parseInt(start, 10) : start) || +1,
        AUTO_SCHEDULE_DAY_END_HOUR + 1
      );

      return hours.map(hour => {
        return {
          label: showLabel
            ? t("End by {{time}}", {
                time: Search.displayTimeFromCardinalHour(hour),
              })
            : Search.displayTimeFromCardinalHour(hour),
          value: hour,
        };
      });
    },

    themeHashToTitle(theme: string) {
      const hashedThemes = ActivitySubjects.Subjects.reduce((map, current) => {
        map[slugifyTitle(current)!] = current;
        return map;
      }, {} as Record<string, string>);
      return translateThemeName(hashedThemes[theme]);
    },

    dateFiltersToString(dateFilters: {
      startAfter?: string | null;
      startBefore?: string | null;
    }) {
      return (
        translateRelativeDateFilter(dateFilters.startAfter) ||
        scheduleFiltersToString(
          ldpick(dateFilters, ["startAfter", "startBefore"])
        )
      );
    },
    getClassSizeFromCapacityFilter(capacityFilters?: {
      capacityMin?: number | null;
      capacityMax?: number | null;
    }) {
      if (capacityFilters?.capacityMax === 1) {
        return "oneOnOne";
      }
      if (capacityFilters?.capacityMin === 2) {
        return "smallGroup";
      }
      return "any";
    },
    getCapacityFilterFromClassSize(
      classSize: string
    ): Search.CapacityFilterType {
      switch (classSize) {
        case "oneOnOne":
          return { capacityMin: undefined, capacityMax: 1 };
        case "smallGroup":
          return { capacityMin: 2, capacityMax: undefined };
        default:
          return { capacityMin: undefined, capacityMax: undefined };
      }
    },
    getClassSizesFromCapacityFilter(capacityFilters?: {
      capacityMin?: number | null;
      capacityMax?: number | null;
    }) {
      const sizes: string[] = [];
      if (capacityFilters?.capacityMax === 1) {
        sizes.push("oneOnOne");
      }
      if (capacityFilters?.capacityMin === 2) {
        sizes.push("smallGroup");
      }
      return sizes;
    },
    getCapacityFiltersFromClassSize(
      classSizes: string[]
    ): Search.CapacityFilterType {
      const capacityMin = classSizes.includes("smallGroup") ? 2 : undefined;
      const capacityMax = classSizes.includes("oneOnOne") ? 1 : undefined;
      return { capacityMin, capacityMax };
    },
  };
}
